// WhatsApp ad-attribution capture — the storefront edge of the attribution flow.
//
// Captures the ad origin of a WhatsApp click and marks the outgoing message so a
// later sale can be traced back to it. The marker is written twice: an invisible
// zero-width prefix, which survives the customer editing the end of the message,
// and a visible ` · ref XXXXXXXX` code, which survives a sanitizer that strips
// zero-width characters. On `go.flavianasser.com.br` the marker is written into
// the redirector's `t` parameter, passed through verbatim into the final message,
// so this module and the incumbent's tag mark the same click.
//
// Ad params are persisted for 90 days (last-touch: every arrival with params
// overwrites the record) because the WhatsApp click rarely happens on the
// landing page.
//
// Capture may fail freely; the click must never break.

const STORAGE_KEY = 'fn-wa-attribution';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

// Hand port of @flavianasser/attribution-token: the theme has no bundler, so the
// package cannot be imported here. Both sides pin the same golden vectors,
// because a silent drift between them breaks recovery without breaking anything
// visible. Names match the package's public surface so the two files read as one
// codec.
//
// One 40-bit integer, three renderings: base 32 over the Crockford alphabet is
// the 8-character token, base 3 is the 26 zero-width digits. The invisible
// prefix survives the customer editing the end of the message; the visible
// ` · ref XXXXXXXX` code is the fallback for the day WhatsApp starts sanitizing
// zero-width characters. 26 digits is the minimum that holds 2^40, since
// 3^25 < 32^8 < 3^26.
//
// Plain Number arithmetic throughout: BigInt is unnecessary (every intermediate
// is far below Number.MAX_SAFE_INTEGER) and 32-bit bitwise operators would
// silently truncate a 40-bit value.
export const TOKEN_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const TOKEN_LENGTH = 8;
export const INVISIBLE_DELIMITER = '\u2060';
export const INVISIBLE_DIGITS = Object.freeze(['\u200B', '\u200C', '\u200D']);
export const INVISIBLE_DIGIT_COUNT = 26;
export const VISIBLE_MARKER_SEPARATOR = ' · ref ';

// The marker format this module wrote before the codec. Still stripped on
// re-click so an old pre-filled text does not carry two references.
const LEGACY_MARKER = /\s*\(ref:\s*[A-Za-z0-9]{1,64}\)/g;

const TOKEN_BYTES = 5;
const TOKEN_SPACE = TOKEN_ALPHABET.length ** TOKEN_LENGTH;

// Built from the constants above so neither the alphabet nor the separator can
// drift out of the pattern: `· ref AAAAAAAI` must yield no candidate, not a
// plausible wrong token. The separator is matched literally and the trailing
// lookahead is the package's own, so this reads exactly the bodies the Node
// package reads.
const VISIBLE_TOKEN_PATTERN = `[${TOKEN_ALPHABET}]{${TOKEN_LENGTH}}(?![\\p{L}\\p{N}])`;
const VISIBLE_RUN = new RegExp(`${VISIBLE_MARKER_SEPARATOR}(${VISIBLE_TOKEN_PATTERN})`, 'gu');

// Looser than VISIBLE_RUN on purpose, and only for removal: this has to eat the
// whitespace in front of the marker, and a marker a hand-edit spaced oddly is
// still ours to strip. Reading stays literal — do not relax VISIBLE_RUN to match.
const VISIBLE_MARKER = new RegExp(`\\s*·\\s*ref\\s+${VISIBLE_TOKEN_PATTERN}`, 'gu');

function valueToToken(value) {
  let remaining = value;
  let token = '';
  for (let position = 0; position < TOKEN_LENGTH; position++) {
    token = TOKEN_ALPHABET[remaining % TOKEN_ALPHABET.length] + token;
    remaining = Math.floor(remaining / TOKEN_ALPHABET.length);
  }
  return token;
}

function tokenToValue(token) {
  if (typeof token !== 'string' || token.length !== TOKEN_LENGTH) return null;
  let value = 0;
  for (const character of token) {
    const digit = TOKEN_ALPHABET.indexOf(character);
    if (digit === -1) return null;
    value = value * TOKEN_ALPHABET.length + digit;
  }
  return value;
}

export function generateToken() {
  // 5 bytes is exactly the 40-bit token space — same draw as the package's
  // randomBytes(5), with no modulo bias over the alphabet.
  const bytes = new Uint8Array(TOKEN_BYTES);
  crypto.getRandomValues(bytes);
  let value = 0;
  for (const byte of bytes) value = value * 256 + byte;
  return valueToToken(value);
}

export function encodeInvisible(token) {
  const value = tokenToValue(token);
  if (value === null) {
    throw new Error(
      `attribution token must be ${TOKEN_LENGTH} characters from the alphabet ${TOKEN_ALPHABET}, received: ${JSON.stringify(token)}`,
    );
  }

  let remaining = value;
  let digits = '';
  for (let position = 0; position < INVISIBLE_DIGIT_COUNT; position++) {
    digits = INVISIBLE_DIGITS[remaining % INVISIBLE_DIGITS.length] + digits;
    remaining = Math.floor(remaining / INVISIBLE_DIGITS.length);
  }
  return `${INVISIBLE_DELIMITER}${digits}${INVISIBLE_DELIMITER}`;
}

// Walked by hand instead of matched by a regex: a character class of zero-width
// joiners is exactly what the misleading-character-class lint rule flags.
//
// Each run resumes the search *at* the closing delimiter, not past it: a
// delimiter that closes one run also opens the next, so a single stray one
// cannot shift the pairing of every run behind it and silently drop a token.
// This mirrors the package's findInvisible exactly.
function findInvisibleRuns(source) {
  const runs = [];
  let start = source.indexOf(INVISIBLE_DELIMITER);
  while (start !== -1) {
    const end = source.indexOf(INVISIBLE_DELIMITER, start + 1);
    if (end === -1) break;
    runs.push({ at: start, digits: source.slice(start + 1, end) });
    start = source.indexOf(INVISIBLE_DELIMITER, end);
  }
  return runs;
}

function isInvisibleDigits(digits) {
  return (
    [...digits].length === INVISIBLE_DIGIT_COUNT &&
    [...digits].every((digit) => INVISIBLE_DIGITS.includes(digit))
  );
}

function decodeInvisibleDigits(digits) {
  if (!isInvisibleDigits(digits)) return null;
  let value = 0;
  for (const digit of digits) {
    value = value * INVISIBLE_DIGITS.length + INVISIBLE_DIGITS.indexOf(digit);
  }
  // 26 base-3 digits address more values than a token can hold, so a run written
  // by a different sender can decode above the range — reject, never fold.
  if (value >= TOKEN_SPACE) return null;
  return valueToToken(value);
}

// No trimming here: the package's buildMessageText does none, and the two must
// produce the same bytes for the same inputs. rewriteWhatsAppHref already hands
// this the output of stripMarkers, which trims.
export function buildMessageText({ token, text }) {
  return `${encodeInvisible(token)}${text || ''}${VISIBLE_MARKER_SEPARATOR}${token}`;
}

export function decodeCandidates(body) {
  const source = typeof body === 'string' ? body : '';
  const found = [];
  for (const run of findInvisibleRuns(source)) {
    const token = decodeInvisibleDigits(run.digits);
    if (token !== null) found.push({ at: run.at, token, via: 'invisible' });
  }

  VISIBLE_RUN.lastIndex = 0;
  let match = VISIBLE_RUN.exec(source);
  while (match !== null) {
    found.push({ at: match.index, token: match[1], via: 'visible' });
    match = VISIBLE_RUN.exec(source);
  }
  found.sort((first, second) => first.at - second.at);

  const candidates = [];
  const seen = new Map();
  for (const entry of found) {
    const known = seen.get(entry.token);
    if (!known) {
      const candidate = { token: entry.token, via: entry.via };
      seen.set(entry.token, candidate);
      candidates.push(candidate);
      continue;
    }
    if (known.via !== entry.via) known.via = 'both';
  }
  return candidates;
}

// Landing-URL query param → camelCase field of the capture payload.
const AD_PARAMS = {
  fbclid: 'fbclid',
  gclid: 'gclid',
  // Google sends these instead of gclid on iOS App and Web-to-App journeys; a
  // click carrying only gbraid used to be stored as if it had no ad origin.
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  ttclid: 'ttclid',
  msclkid: 'msclkid',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
};

export function extractAdParams(search) {
  const query = new URLSearchParams(search || '');
  const params = {};
  for (const [param, field] of Object.entries(AD_PARAMS)) {
    const value = query.get(param);
    if (value) params[field] = value;
  }
  return params;
}

// Storage can be unavailable (private mode, blocked cookies) — attribution then
// degrades to same-page capture only, never to a broken page.
export function rememberArrival(storage, search, nowMs) {
  const params = extractAdParams(search);
  if (!Object.keys(params).length) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ params, savedAt: nowMs }));
  } catch {
    // ignore
  }
}

export function loadStoredParams(storage, nowMs) {
  let record;
  try {
    record = JSON.parse(storage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
  if (!record || typeof record.params !== 'object' || !record.params) return null;
  if (typeof record.savedAt !== 'number' || nowMs - record.savedAt > TTL_MS) return null;
  return Object.keys(record.params).length ? record.params : null;
}

export function readCookie(cookieString, name) {
  for (const part of (cookieString || '').split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    const raw = part.slice(eq + 1).trim();
    // A malformed percent sequence makes decodeURIComponent throw, and both
    // cookie reads happen before any guard in captureClick — one broken cookie
    // written by anyone on the domain would cost the whole capture. The raw
    // value is worth more than nothing: `_fbc` is an ad origin on its own.
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

// Removes every marker this module could have written — the invisible run, our
// visible code, and the ` (ref: …)` form that preceded them — so a re-click
// carries exactly one fresh token instead of a pile of stale ones.
//
// Only a run that decodes to a token is removed; anything else the customer
// typed is theirs and stays put. Runs are consumed left to right, and a run that
// starts inside the one already consumed *extends* the removal instead of being
// skipped. Skipping it would strand its digits and its closing delimiter in the
// text, and the fresh prefix written next would pair against that orphan — one
// click, two candidates, the second one stale. Two markers share a delimiter
// exactly when something collapsed the two adjacent word joiners between them,
// which is the same sanitizer behavior the delimiter pairing in
// findInvisibleRuns already exists to survive.
export function stripMarkers(text) {
  const source = text || '';
  let stripped = '';
  let cursor = 0;
  for (const run of findInvisibleRuns(source)) {
    if (decodeInvisibleDigits(run.digits) === null) continue;
    const runEnd = run.at + run.digits.length + 2;
    if (runEnd <= cursor) continue;
    if (run.at > cursor) stripped += source.slice(cursor, run.at);
    cursor = runEnd;
  }
  stripped += source.slice(cursor);
  return stripped.replace(VISIBLE_MARKER, '').replace(LEGACY_MARKER, '').trim();
}

function decodeValue(raw) {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}

// Rewrites only the destination's message parameter, leaving every other byte of
// the href alone. Done by hand on the raw query string on purpose: URLSearchParams
// re-serializes everything it touches, and the redirector's `lp` carries a
// percent-encoded URL that must reach it exactly as its own tag wrote it.
export function rewriteWhatsAppHref(href, token) {
  const param = whatsAppTextParam(href);
  if (!param) return href;

  const hashAt = href.indexOf('#');
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  const base = hashAt === -1 ? href : href.slice(0, hashAt);
  const queryAt = base.indexOf('?');
  const path = queryAt === -1 ? base : base.slice(0, queryAt);
  const query = queryAt === -1 ? '' : base.slice(queryAt + 1);

  let replaced = false;
  const parts = (query ? query.split('&') : []).map((part) => {
    const eq = part.indexOf('=');
    const key = eq === -1 ? part : part.slice(0, eq);
    if (decodeValue(key) !== param) return part;
    replaced = true;
    const text = stripMarkers(eq === -1 ? '' : decodeValue(part.slice(eq + 1)));
    return `${key}=${encodeURIComponent(buildMessageText({ token, text }))}`;
  });
  if (!replaced) {
    parts.push(`${param}=${encodeURIComponent(buildMessageText({ token, text: '' }))}`);
  }

  return `${path}?${parts.join('&')}${hash}`;
}

export function buildPayload({
  token,
  storedParams,
  fbp,
  fbc,
  product,
  pageUrl,
  clickDestination,
}) {
  const payload = { token, ...(storedParams || {}) };
  if (fbp) payload.fbp = fbp;
  if (fbc) payload.fbc = fbc;
  if (product) payload.product = product;
  if (pageUrl) payload.pageUrl = pageUrl;
  if (clickDestination) payload.clickDestination = clickDestination;
  return payload;
}

// Destination host → the query parameter whose value becomes the WhatsApp
// message, and the label the capture records for it. `wa.me` 302s to
// `api.whatsapp.com`, so both are reachable from a click; they are separate
// labels because that 302 is itself a suspect for losing the invisible marker.
// `go.flavianasser.com.br` is the incumbent tag's redirector: it rewrites every
// CTA at page load and passes its own `t` through verbatim into the final
// message, which is what lets both attribution systems mark the same click.
// Matching is exact hostname equality, which is what keeps a lookalike such as
// `whatsapp.com.evil.test` out.
//
// One entry carries both facts so a host cannot be reached without a label. The
// capture endpoint validates the label against a closed set and rejects the
// whole body on a value outside it — a new host here is a dropped capture until
// its label is accepted there first.
// Frozen entry by entry, not just at the top: Object.freeze is shallow, and the
// map is exported, so a mutable entry would let an importer reach a label the
// endpoint refuses — the one hole the test derived from this map cannot see,
// because it reads the map after any mutation.
export const WHATSAPP_DESTINATIONS = Object.freeze({
  'wa.me': Object.freeze({ textParam: 'text', label: 'wa_me' }),
  'api.whatsapp.com': Object.freeze({ textParam: 'text', label: 'whatsapp_api' }),
  'go.flavianasser.com.br': Object.freeze({ textParam: 't', label: 'redirector' }),
});

function whatsAppDestinationEntry(href) {
  try {
    const { hostname } = new URL(href);
    return Object.prototype.hasOwnProperty.call(WHATSAPP_DESTINATIONS, hostname)
      ? WHATSAPP_DESTINATIONS[hostname]
      : null;
  } catch {
    return null;
  }
}

export function whatsAppTextParam(href) {
  const entry = whatsAppDestinationEntry(href);
  return entry ? entry.textParam : null;
}

export function whatsAppDestination(href) {
  const entry = whatsAppDestinationEntry(href);
  return entry ? entry.label : null;
}

export function isWhatsAppLink(href) {
  return whatsAppTextParam(href) !== null;
}

function readConfig() {
  const island = document.querySelector('[data-wa-attribution-config]');
  if (!island) return null;
  try {
    const config = JSON.parse(island.textContent);
    return config && config.endpoint && config.token ? config : null;
  } catch {
    return null;
  }
}

// The label is read before the request goes out, so it needs the same guard the
// rewrite below has: `link` is a live DOM node whose `href` can throw. A capture
// with no label is worth more than no capture at all.
function readClickDestination(link) {
  try {
    return whatsAppDestination(link.href);
  } catch {
    return null;
  }
}

// keepalive lets the request outlive the navigation; sendBeacon cannot carry the
// auth header. Nothing in here throws: the request and the href assignment are
// guarded separately, because the click must win even when the capture does not.
// The href write needs its own guard rather than riding on the caller's — `link`
// is a live DOM node whose `href` can throw, and this function is exported, so
// its totality cannot depend on `init` being the only caller. The environment
// arrives as arguments so this is a plain unit under test, with no DOM and no
// globals.
export function captureClick({
  link,
  config,
  fetchImpl,
  storage,
  cookieString,
  pageUrl,
  nowMs,
  token,
}) {
  const storedParams = loadStoredParams(storage, nowMs);
  const fbp = readCookie(cookieString, '_fbp');
  // `_fbc` is written by the Meta pixel on an arrival carrying `fbclid` and
  // lasts 90 days, so it outlives the session the stored params do not. It is
  // an ad origin on its own: a visitor who arrived from an ad, left, and came
  // back direct has the cookie and nothing else.
  const fbc = readCookie(cookieString, '_fbc');
  if (!storedParams && !fbp && !fbc) return null;

  const clickToken = token || generateToken();
  const payload = buildPayload({
    token: clickToken,
    storedParams,
    fbp,
    fbc,
    product: config.product,
    pageUrl,
    clickDestination: readClickDestination(link),
  });

  try {
    const sent = fetchImpl(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-webhook-token': config.token,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (sent && typeof sent.catch === 'function') sent.catch(() => {});
  } catch {
    // ignore
  }

  try {
    link.href = rewriteWhatsAppHref(link.href, clickToken);
  } catch {
    // The capture row is already sent and will simply never be matched — an
    // unattributed token upstream, which is the harmless direction.
    return null;
  }
  return clickToken;
}

function init() {
  const config = readConfig();
  if (!config) return;

  rememberArrival(window.localStorage, window.location.search, Date.now());

  // Capture-phase delegation so every WhatsApp anchor — including the ones the
  // incumbent's tag rewrites after load — is covered before any other handler
  // can stop the event.
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element && event.target.closest('a[href]');
      if (!link || !isWhatsAppLink(link.href)) return;
      try {
        captureClick({
          link,
          config,
          fetchImpl: window.fetch.bind(window),
          storage: window.localStorage,
          cookieString: document.cookie,
          pageUrl: window.location.href,
          nowMs: Date.now(),
        });
      } catch {
        // never break the click
      }
    },
    true,
  );
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
